import {
  getCanAddIntegrationChannelsMetadata,
  getIsEditableIntegration,
  getIsIntegrationEditOptional,
} from '@modules/integrationsV2/utils';
import { Box, Stack } from '@mui/material';
import { IntegrationChannelType, IntegrationType } from 'generated/graphql';

interface GetSuccessMessageForAddOrEditIntegrationParams {
  channelType: IntegrationChannelType;
  integrationType: IntegrationType;
  action: 'add' | 'edit';
  isIntegrationContextEnabled?: boolean;
}

export const getSuccessMessageForAddOrEditIntegration = ({
  channelType,
  integrationType,
  action,
  isIntegrationContextEnabled = false,
}: GetSuccessMessageForAddOrEditIntegrationParams) => {
  const isEditableIntegration = getIsEditableIntegration({
    channelType,
    integrationData: undefined,
    integrationType,
  });

  const isIntegrationEditOptional = getIsIntegrationEditOptional({
    channelType,
  });

  const isFeedbackType = integrationType === IntegrationType.Feedback;

  const defaultMessage = (
    <Stack spacing={1}>
      <Box>
        Your changes are saved! We'll start pulling{' '}
        {isFeedbackType ? 'fresh feedback' : 'data'} for this integration.
      </Box>
      <Box>
        It might take up to 24 hours for {isFeedbackType ? 'feedback' : 'data'}{' '}
        to be available for analysis.
      </Box>
    </Stack>
  );

  if (
    action === 'edit' &&
    getCanAddIntegrationChannelsMetadata(channelType) &&
    isIntegrationContextEnabled
  ) {
    return defaultMessage;
  }

  if (action === 'edit' && isIntegrationEditOptional) {
    return <Box>Your changes are saved!</Box>;
  }

  if (action === 'add' && isEditableIntegration && !isIntegrationEditOptional) {
    return null; // Instead we show it during edit call, when integration is actually completed
  }

  if (channelType === IntegrationChannelType.FileUpload) {
    return (
      <Stack spacing={1}>
        <Box>Your File Upload integration is successfully created!</Box>
        <Box>
          You can upload your file to start analyzing your feedback data.
        </Box>
      </Stack>
    );
  }

  if (channelType === IntegrationChannelType.Webhook) {
    return (
      <Stack spacing={1}>
        <Box>Your Webhook integration is successfully created!</Box>
        <Box>
          You can copy the API key at the top of the page to start sending data
          to Enterpret.
        </Box>
      </Stack>
    );
  }

  return defaultMessage;
};
